import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getEmails, addEmail, deleteEmail } from "../../redux/thunk";
import { resetAddEmailStatus, resetDeleteEmailStatus } from "../../redux/inventory/inventorySlice";
import LoadingSpinner from "../../components/elements/LoadingSpinner";
import { IoSearchOutline, IoTrashOutline } from "react-icons/io5";
import toast from "react-hot-toast";

const SalesEmails: React.FC = () => {
  const dispatch = useAppDispatch();
  const { emails, addEmail: addEmailState, deleteEmail: deleteEmailState } = useAppSelector(
    (state) => state.inventory
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getEmails());
  }, [dispatch]);

  useEffect(() => {
    if (addEmailState.status === "success") {
      setNewEmail("");
      setIsAddModalOpen(false);
      dispatch(resetAddEmailStatus());
      dispatch(getEmails()); // Refresh list after adding
    }
  }, [addEmailState.status, dispatch]);

  useEffect(() => {
    if (deleteEmailState.status === "success") {
      dispatch(resetDeleteEmailStatus());
      dispatch(getEmails()); // Refresh list after deletion
    }
  }, [deleteEmailState.status, dispatch]);

  const handleAddEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail) {
      toast.error("Please enter an email address");
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      toast.error("Please enter a valid email address");
      return;
    }
    // Check for duplicate email
    const emailExists = emails.data.some(
      (email) => email.email.toLowerCase() === newEmail.toLowerCase()
    );
    if (emailExists) {
      toast.error("This email address has already been added");
      return;
    }
    dispatch(addEmail(newEmail));
  };

  const handleDeleteEmail = (id: string) => {
    if (window.confirm("Are you sure you want to delete this email?")) {
      dispatch(deleteEmail(id));
    }
  };

  const filteredEmails = emails.data.filter((email) =>
    email.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <main className="overflow-x-auto w-full">
      <div className="bg-white rounded-md shadow-md pb-6">
        <div className="w-full overflow-x-auto" style={{ minWidth: "700px" }}>
          <h1 className="sr-only">E-mails</h1>

          <div className="flex flex-col gap-4 px-6 pt-6 pb-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
            <div className="relative w-full flex-1 sm:max-w-2xl">
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search E-mails"
                className="w-full rounded-lg border border-gray-200 py-2.5 pl-4 pr-10 text-sm text-gray-900 outline-none transition-shadow focus:border-[#B5572C] focus:ring-1 focus:ring-[#B5572C]"
                aria-label="Search e-mails"
              />
              <IoSearchOutline
                className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400"
                aria-hidden
              />
            </div>
            <button
              type="button"
              onClick={() => setIsAddModalOpen(true)}
              className="shrink-0 rounded-lg bg-green-700 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-800"
            >
              +Add Email
            </button>
          </div>

          <div className="px-6 pb-2">
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/90 text-gray-600">
                    <th className="px-5 py-3 text-left font-medium">S/N</th>
                    <th className="px-5 py-3 text-center font-medium">
                      Email Address
                    </th>
                    <th className="px-5 py-3 text-right font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.status === "loading" ? (
                    <tr>
                      <td colSpan={3} className="p-12 text-center">
                        <LoadingSpinner />
                      </td>
                    </tr>
                  ) : emails.data.length === 0 ? (
                    <tr>
                      <td
                        colSpan={3}
                        className="p-12 text-center text-gray-500 italic"
                      >
                        No emails found!
                      </td>
                    </tr>
                  ) : filteredEmails.length > 0 ? (
                    filteredEmails.map((email, index) => (
                      <tr
                        className="transition-colors hover:bg-gray-50/60"
                        key={email._id}
                      >
                        <td className="px-5 py-4 text-left font-medium text-gray-700 tabular-nums">
                          {String(index + 1).padStart(2, "0")}
                        </td>
                        <td className="px-5 py-4 text-center text-gray-900">
                          {email.email}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => handleDeleteEmail(email._id)}
                            className="inline-flex rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={deleteEmailState.status === "loading"}
                            aria-label="Delete email"
                          >
                            <IoTrashOutline className="h-5 w-5" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={3}
                        className="p-12 text-center text-gray-500 italic"
                      >
                        No emails match your search
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Add Email Modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]">
          <div
            className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl sm:p-10"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-email-title"
          >
            <h2
              id="add-email-title"
              className="text-lg font-semibold tracking-tight text-gray-900 sm:text-xl"
            >
              Add New Email
            </h2>

            <form onSubmit={handleAddEmail} className="mt-8">
              <div>
                <label
                  htmlFor="email"
                  className="mb-2 block text-sm font-medium text-gray-900"
                >
                  E-mail
                </label>
                <input
                  type="email"
                  id="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition-colors placeholder:text-gray-400 focus:border-gray-300 focus:ring-2 focus:ring-gray-100"
                  placeholder=""
                  autoComplete="email"
                  required
                />
              </div>

              <div className="mt-10 flex flex-wrap items-center justify-start gap-3">
                <button
                  type="submit"
                  disabled={addEmailState.status === "loading"}
                  className="rounded-lg bg-green-800 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-green-900 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {addEmailState.status === "loading" ? "Adding…" : "Add E-mail"}
                </button>
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="rounded-lg border border-rose-400 bg-white px-5 py-2.5 text-sm font-semibold text-rose-600 transition-colors hover:bg-rose-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default SalesEmails;
